<template>
  <component
    :is="componentTag"
    :class="buttonClasses"
    :style="customColorStyles"
    v-bind="dynamicAttributes"
    :type="buttonType"
    @click="handleClick"
  >
    <!-- Loading Spinner (highest priority) -->
    <span
      v-if="isLoading"
      :class="[
        'loading-spinner',
        $slots.rightIcon || rightIcon ? 'icon icon-right' : '',
      ]"
    ></span>

    <!-- Left Icon (only when not loading) -->
    <span v-else-if="$slots.icon || icon" class="icon icon-left">
      <slot name="icon">
        <eg-icon v-if="icon" :icon="icon" :size="iconSize" :color="iconColor" />
      </slot>
    </span>

    <!-- Button Text -->
    <span v-if="showText" class="button-text">
      <template v-if="isLoading">
        {{ loadingText }}
      </template>
      <template v-else>
        <slot></slot>
      </template>
    </span>

    <!-- Right Icon (only when not loading and no left icon) -->
    <span
      v-if="!isLoading && ($slots.rightIcon || rightIcon)"
      class="icon icon-right"
    >
      <slot name="rightIcon">
        <eg-icon
          v-if="rightIcon"
          :icon="rightIcon"
          :size="iconSize"
          :color="iconColor"
        />
      </slot>
    </span>
  </component>
</template>

<script>
export default {
  name: "EgButton",
  props: {
    type: {
      type: String,
      default: "ordinary",
      validator: (value) =>
        ["primary", "secondary", "ordinary"].includes(value),
    },
    buttonType: {
      type: String,
      default: "button",
      validator: (value) => ["button", "submit", "reset"].includes(value),
    },
    size: {
      type: String,
      default: "medium",
      validator: (value) =>
        ["extra-small", "small", "medium", "sm", "md", "lg"].includes(value),
    },
    destructive: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "purple",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    loadingText: {
      type: String,
      default: "Loading…",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    to: {
      type: [String, Object],
      default: undefined,
    },
    href: {
      type: String,
      default: undefined,
    },
    target: {
      type: String,
      default: undefined,
    },
    icon: {
      type: String,
      default: undefined,
    },
    rightIcon: {
      type: String,
      default: undefined,
    },
    iconSize: {
      type: String,
      default: "small",
      validator: (value) =>
        ["extra-small", "small", "extra-medium", "medium", "large"].includes(
          value
        ),
    },
    iconColor: {
      type: String,
      default: undefined,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click"],
  computed: {
    isExternalLink() {
      return !!this.href;
    },
    isInternalLink() {
      return !!this.to && !this.href;
    },
    isLink() {
      return this.isExternalLink || this.isInternalLink;
    },
    componentTag() {
      if (this.isExternalLink) return "a";
      if (this.isInternalLink) return "router-link";
      return "button";
    },
    buttonClasses() {
      return [
        "eg-new-button",
        `eg-new-button--${this.type}`,
        `eg-new-button--${this.size}`,
        `eg-new-button--color-${this.normalizedColor}`,
        {
          "eg-new-button--loading": this.isLoading,
          "eg-new-button--disabled": this.disabled,
          "eg-new-button--icon-only": this.isIconOnly,
          "eg-new-button--destructive": this.destructive,
          "eg-new-button--active": this.active,
        },
      ];
    },
    normalizedColor() {
      if (this.color === "purple" || this.color === "danger") {
        return this.color;
      }
      return "custom";
    },
    customColorStyles() {
      if (this.color !== "purple" && this.color !== "danger") {
        return { "--custom-color": this.color };
      }
      return {};
    },

    hasSlotText() {
      return (
        this.$slots.default &&
        (this.$slots.default?.[0]?.tag ||
          this.$slots.default?.[0]?.text?.trim().length > 0)
      );
    },

    showText() {
      // Don't show text for icon-only buttons, even when loading
      if (this.isIconOnlyButton) {
        return false;
      }

      return this.hasSlotText || this.isLoading;
    },
    isIconOnlyButton() {
      const hasIcon =
        this.$slots.icon ||
        this.$slots.rightIcon ||
        this.icon ||
        this.rightIcon;

      return hasIcon && !this.hasSlotText;
    },
    isIconOnly() {
      return this.isIconOnlyButton;
    },
    dynamicAttributes() {
      const attrs = {};

      if (this.isExternalLink) {
        attrs.href = this.href;
        if (this.target) {
          attrs.target = this.target;
        }
      } else if (this.isInternalLink) {
        attrs.to = this.to;
      } else {
        attrs.disabled = this.disabled || this.isLoading;
      }

      return attrs;
    },
  },
  methods: {
    handleClick(event) {
      // For external links and router-link, let browser/router handle navigation
      if (this.isLink) {
        return;
      }

      if (!this.disabled && !this.isLoading) {
        this.$emit("click", event);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.eg-new-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  cursor: pointer;
  transition: background-color 1.4s cubic-bezier(0.25, 1, 0.5, 1),
    transform 100ms ease-in-out;
  position: relative;
  text-decoration: none;
  box-sizing: border-box;
  background: none;
  outline: none;
  white-space: normal;

  &:focus-visible {
    outline: 2px solid var(--color-stroke-primary);
  }

  // Size variants
  &--extra-small,
  &--sm {
    padding: 7px 10px;
    font-size: 12px;
    line-height: 14px;
    gap: 4px;

    .icon {
      svg {
        width: 12px;
        height: 12px;
      }
    }
  }

  &--small,
  &--md {
    padding: 7px 12px;
    font-size: 12px;
    line-height: 14px;
    gap: 6px;

    .icon {
      svg {
        width: 14px;
        height: 14px;
      }
    }
  }

  &--medium {
    padding: 8px 16px;
    font-size: 14px;
    gap: 8px;
  }

  // Icon-only button
  &--icon-only {
    width: 36px; // medium (default)
    height: 36px;
    padding: 0;

    .icon {
      svg {
        height: 16px;
        width: 16px;
      }
    }

    &.eg-new-button--small {
      width: 32px;
      height: 32px;
    }

    &.eg-new-button--extra-small {
      width: 24px;
      height: 24px;
    }
  }

  // Primary type
  &--primary {
    &.eg-new-button--color-purple {
      background-color: var(--color-surface-primary-default);
      color: white;
      border-color: var(--color-stroke-primary);

      &:hover:not(:disabled) {
        background-color: var(--color-surface-primary-hover);
        border-color: var(--color-surface-primary-hover);
      }

      &:active:not(:disabled) {
        background-color: var(--color-surface-primary-active);
        border-color: var(--color-surface-primary-active);
        transform: translateY(1px);
      }
      &:focus-visible:not(:hover) {
        outline: 2px solid var(--color-stroke-primary);
      }
      &:focus-visible:hover {
        outline: 2px solid var(--color-surface-primary-hover);
      }
    }

    &.eg-new-button--color-danger {
      background-color: var(--color-surface-semantic-error-error);
      color: white;
      border-color: var(--color-stroke-semantic-onError);

      &:hover:not(:disabled) {
        background-color: var(--color-surface-semantic-error-hover);
        border-color: var(--color-surface-semantic-error-hover);
      }

      &:active:not(:disabled) {
        background-color: var(--color-surface-semantic-error-press);
        border-color: var(--color-surface-semantic-error-press);
        transform: translateY(1px);
      }
    }

    &.eg-new-button--color-custom {
      background-color: var(--custom-color);
      color: white;
      border-color: var(--custom-color);

      &:hover:not(:disabled) {
        filter: brightness(0.9);
      }

      &:active:not(:disabled) {
        filter: brightness(0.8);
        transform: translateY(1px);
      }
    }
  }

  // Secondary type
  &--secondary {
    &.eg-new-button--color-purple {
      background-color: transparent;
      color: var(--color-stroke-primary);
      border-color: var(--color-stroke-primary);

      &:hover:not(:disabled) {
        background-color: var(--color-surface-neutral-hover);
        color: var(--color-surface-primary-hover);
        border-color: var(--color-surface-primary-hover);
      }

      &:active:not(:disabled) {
        background-color: var(--color-surface-neutral-press);
        color: var(--color-surface-primary-active);
        border-color: var(--color-surface-primary-active);
        transform: translateY(1px);
      }
    }

    &.eg-new-button--color-danger {
      background-color: transparent;
      color: var(--color-surface-semantic-error-error);
      border-color: var(--color-surface-semantic-error-error);

      &:hover:not(:disabled) {
        background-color: var(--colors-red-50);
        color: var(--color-surface-semantic-error-hover);
        border-color: var(--color-surface-semantic-error-hover);
      }

      &:active:not(:disabled) {
        background-color: var(--colors-red-100);
        color: var(--color-surface-semantic-error-press);
        border-color: var(--color-surface-semantic-error-press);
        transform: translateY(1px);
      }
    }

    &.eg-new-button--color-custom {
      background-color: transparent;
      color: var(--custom-color);
      border-color: var(--custom-color);

      &:hover:not(:disabled) {
        background-color: rgba(0, 0, 0, 0.05);
        filter: brightness(0.9);
      }

      &:active:not(:disabled) {
        background-color: rgba(0, 0, 0, 0.1);
        filter: brightness(0.8);
        transform: translateY(1px);
      }
    }
  }

  // Ordinary type
  &--ordinary {
    background-color: transparent;
    color: var(--color-stroke-primary);
    border-color: transparent;

    &:hover:not(:disabled) {
      background-color: var(--color-surface-neutral-hover);
      color: var(--color-stroke-primary);
    }

    &:active:not(:disabled) {
      background-color: var(--color-surface-neutral-press);
      color: var(--color-stroke-primary);
      transform: translateY(1px);
    }

    &:focus-visible {
      outline: 2px solid var(--color-stroke-primary);
    }

    &.eg-new-button--color-custom {
      background-color: transparent;
      color: var(--custom-color);

      &:hover:not(:disabled) {
        background-color: transparent;
        filter: brightness(0.9);
      }

      &:active:not(:disabled) {
        background-color: transparent;
        filter: brightness(0.8);
        transform: translateY(1px);
      }
    }
  }

  // Destructive modifier - applies red colors to any type
  &--destructive {
    &.eg-new-button--primary {
      background-color: var(--color-surface-destructive-default);
      border-color: var(--color-border-destructive);

      &:hover:not(:disabled) {
        background-color: var(--color-surface-destructive-hover);
        border-color: var(--color-surface-destructive-hover);
      }

      &:active:not(:disabled) {
        background-color: var(--color-surface-destructive-active);
        border-color: var(--color-surface-destructive-active);
      }

      &:focus-visible {
        outline: 2px solid var(--color-stroke-primary);
      }
    }

    &.eg-new-button--secondary {
      color: var(--color-border-destructive);
      border-color: var(--color-border-destructive);

      &:hover:not(:disabled) {
        background-color: var(--colors-red-50);
        color: var(--color-surface-destructive-hover);
        border-color: var(--color-surface-destructive-hover);
      }

      &:active:not(:disabled) {
        background-color: var(--colors-red-100);
        color: var(--color-surface-destructive-active);
        border-color: var(--color-surface-destructive-active);
      }

      &:focus-visible {
        outline: 2px solid var(--color-stroke-primary);
      }
    }

    &.eg-new-button--ordinary {
      color: var(--color-border-destructive);

      &:hover:not(:disabled) {
        background-color: var(--colors-red-50);
        color: var(--color-border-destructive);
      }

      &:active:not(:disabled) {
        background-color: var(--colors-red-100);
        color: var(--color-border-destructive);
      }

      &:focus-visible {
        outline: 2px solid var(--color-stroke-primary);
      }
    }
  }

  // States
  &:disabled:not(.eg-new-button--loading),
  &--disabled:not(.eg-new-button--loading) {
    cursor: not-allowed;
    pointer-events: none;
  }

  // Primary disabled - neutral disabled background with grey text
  &--primary:disabled:not(.eg-new-button--loading),
  &--primary.eg-new-button--disabled:not(.eg-new-button--loading) {
    background-color: var(--color-surface-neutral-disabled);
    color: var(--color-text-neutral-disabled);
    border-color: var(--color-stroke-neutral-default);
  }

  // Secondary disabled - grey border with grey text
  &--secondary:disabled:not(.eg-new-button--loading),
  &--secondary.eg-new-button--disabled:not(.eg-new-button--loading) {
    background-color: transparent;
    color: var(--color-text-neutral-disabled);
    border-color: var(--color-surface-neutral-container-6);
  }

  // Ordinary disabled - grey text on transparent
  &--ordinary:disabled:not(.eg-new-button--loading),
  &--ordinary.eg-new-button--disabled:not(.eg-new-button--loading) {
    background-color: transparent;
    color: var(--color-text-neutral-disabled);
    border-color: transparent;
  }

  // Destructive disabled - neutral disabled background with grey text
  &--destructive:disabled:not(.eg-new-button--loading),
  &--destructive.eg-new-button--disabled:not(.eg-new-button--loading) {
    &.eg-new-button--primary {
      background-color: var(--color-surface-neutral-disabled);
      color: var(--color-text-neutral-disabled);
      border-color: var(--color-surface-neutral-disabled);
    }

    &.eg-new-button--secondary {
      background-color: transparent;
      color: var(--color-text-neutral-disabled);
      border-color: var(--color-surface-neutral-container-6);
    }

    &.eg-new-button--ordinary {
      background-color: transparent;
      color: var(--color-text-neutral-disabled);
      border-color: transparent;
    }
  }

  &--loading {
    cursor: not-allowed;

    .button-text {
      opacity: 0.7;
    }
  }

  // Icon styles
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;

    &.icon-right {
      order: 3;
    }

    // Support for new eg-icon font icons
    .eg-icon {
      color: currentColor;
    }

    // Legacy SVG support
    svg {
      fill: currentColor;
      width: 16px;
      height: 16px;
    }
  }

  // Loading spinner
  .loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  &--small .loading-spinner {
    width: 12px;
    height: 12px;
    border-width: 1.5px;
  }

  // Hide icons when loading for icon-only buttons
  &--loading.eg-new-button--icon-only .icon {
    display: none;
  }

  // Remove margin for spinner in icon-only buttons
  &--icon-only .loading-spinner {
    margin-left: 0;
  }

  &--active {
    background-color: var(--color-surface-neutral-press);
    color: var(--color-stroke-primary);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
